import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { Modal } from 'semantic-ui-react';
import reportIcon from '../../assets/report.svg';
import BandMediaHelpModal from '../../../BandMediaHelpModal/BandMediaHelpModal';
import BandMediaAPI from '../../../../api/BandMediaAPI';
import ToastMessage from '../../../ToastMessage/ToastMessage';
import background_yellow from '../../assets/backgrounds/boxes_yellow.svg';
import background_blue from '../../assets/backgrounds/boxes_blue.svg';
import contacts from '../../../../consts/contacts';

import '../Slides.scss'
import './ImageSlide.scss';

const ImageSlide = ({ data, colorOption, kidId, grownUpId, isAndroid  }) => {

  const {
    title,
    photo_url,
    photo_media_submission_id,
    date
  } = data;

  const [reportModelOpen, setReportModelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toastSuccess, setToastSuccess] = useState(false);
  const [displayToastMessage, setDisplayToastMessage] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const handleImageClick = () => {
    setIsFullScreen(true);
  };

  // REPORT OBJECT (layout required for api)
  const reportConfig = (comment) => {
    const config = {
        data: {
          band_media_discrepancy_report: {
            comment,
            band_media_submission_id: photo_media_submission_id,
            kid_id: kidId,
            grown_up_id: grownUpId
        }
      }
    };
    return config;
  }

  // UPDATE MESSAGE FROM REPORT
  const setToastType = (result) => {
    setDisplayToastMessage(true);
    setToastSuccess(result);
  }

  // CLOSE REPORT MODEL
  const resetModal = () => {
    setIsLoading(false);
    setReportModelOpen(false)
  }

  // TRIGGER REPORT API
  const reportMedia = (comment) => {
    const config = reportConfig(comment);
    BandMediaAPI.reportMediaDiscrepancy(config).then(() => {
      setToastType(true);
    }).catch(() => setToastType(false)).finally(() => resetModal());
  }

  // OPEN REPORT MODEL
  const triggerReport = () => {
    setReportModelOpen(true)
  }

  const renderToastMessage = () => {
    if (!displayToastMessage) { return null }
    let content = (
      <ToastMessage
        title="Something went wrong"
        body={`Please try again in a moment or contact us by phone on: ${contacts.SUPPORT_TEL_HUMANIZED}`}
        iconName="exclamation"
        iconColor="#942d29"
        backgroundColor="#ff8985"
        textColor="#521715"
      />
    )

    if (toastSuccess) {
      content = <ToastMessage title="Thanks for reporting" body="A member of staff will review your report shortly." />
    } 
    return (
      <div className='Slide__image__reportMessage'>{content}</div>
    )
  }

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    config: {
      tension: 180,
      friction: 12,
    },
    delay: 400,
  });

  const dateSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 200, friction: 25 },
    delay: 600,
  });

  const imageSpring = useSpring({
    from: { 
      opacity: 0, 
      transform: 'scale(0.5) translateY(50px)', 
      pointerEvents: 'none',
    },
    to: { 
      opacity: 1, 
      transform: 'scale(1) translateY(0px)', 
      pointerEvents: 'auto',
    },
    config: { 
      tension: 300, 
      friction: 12 
    },
    delay: 1200,
  });

  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 900,
  });

  const appear = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 1000,
  })

  const backgroundScale = useSpring({
    loop: { reverse: true },
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.1)' },
    config: { duration: 8000 },
  });

  const backgroundSpring = {
    ...appear,
    ...backgroundScale
  }

  const reportButtonSpring = useSpring({
    ...appear,
    delay: 1200
  })


  return (
    <div className='Slide__image' style={{backgroundColor: colorOption === 1 ? '#F8AB45' : '#69C3C6'}}>
      <animated.h1 className='Slide__image__title' style={titleSpring}>{title}</animated.h1>
      <Modal
        open={isFullScreen}
        onClose={() => setIsFullScreen(false)}
      >
        <animated.img className='Slide__image__fullScreenAsset' src={photo_url} alt="fullscreen" />
      </Modal>
      {!toastSuccess && !displayToastMessage  && (
        <animated.img className='Slide__image__asset' style={imageSpring} src={photo_url} onClick={handleImageClick} alt="slide_image" />  
      )}
      <button
        type='button'
        onClick={triggerReport}
        className='Slide__image__report'
        style={{
          ...reportButtonSpring,
          bottom: isAndroid ? '80px' : '20px'
        }}
      >
        <ReactSVG src={reportIcon} />
      </button>
      <BandMediaHelpModal
        displayModal={reportModelOpen}
        closeModal={() => setReportModelOpen(false)}
        onReport={reportMedia}
        isLoading={isLoading}
        setLoadingState={setIsLoading}
      />
      {renderToastMessage()}
      <animated.div
        className='Slide__image__box'
        style={{
          ...boxSpring,
          backgroundColor: colorOption === 1 ? '#EC660D' : '#338988'
        }}
      >
        <animated.h3
          className='Slide__image__caption'
          style={{
            ...dateSpring,
            paddingBottom: isAndroid ? '20vh' : '15vh'
          }}
        >
          {date}
        </animated.h3>
      </animated.div>
      <animated.div className='Slide__image__background' style={backgroundSpring}>
        <ReactSVG src={colorOption === 1 ? background_yellow : background_blue} />
      </animated.div>
    </div>
  )
}

export default ImageSlide;

const DataProps = {
  title: PropTypes.string.isRequired,
  photo_url: PropTypes.string.isRequired,
  photo_media_submission_id: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
}

ImageSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired,
  colorOption: PropTypes.number.isRequired,
  kidId: PropTypes.string.isRequired,
  grownUpId: PropTypes.string.isRequired,
  isAndroid: PropTypes.bool
}


ImageSlide.defaultProps = {
  isAndroid: false
}